import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { useApplications } from '../hooks/useApplications'
import { useImageUpload } from '../hooks/useImageUpload'
import { OAUTH_PROVIDERS, REQUIRABLE_PROVIDERS, type OAuthProvider, type RequirableProvider, type Application } from '../types/database'
import {
  VStack,
  HStack,
  Card,
  Heading,
  Text,
  Button,
  IconButton,
  Input,
  Textarea,
  Checkbox,
  Switch,
  Select,
  CopyButton,
  ColorPicker,
  FileUpload,
  Animate,
  Tabs,
  TabPanels,
  TabPanel
} from 'wss3-forge'
import {
  ArrowLeft20Regular,
  Save20Regular,
  Eye20Regular,
  EyeOff20Regular,
  ArrowSync20Regular,
  Delete20Regular,
  ShieldLock20Regular,
  Info20Regular,
  Key20Regular,
  People20Regular,
  Add20Regular,
  Dismiss20Regular
} from '@fluentui/react-icons'

export function ApplicationForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  usePageTitle(isEdit ? 'Edit Application' : 'New Application')

  const { applications, createApplication, updateApplication, regenerateSecret } = useApplications()
  const { uploading, uploadImage, deleteImage } = useImageUpload()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [themeColor, setThemeColor] = useState('#A35BFF')
  const [allowedProviders, setAllowedProviders] = useState<OAuthProvider[]>(['email'])
  const [requiredProvider, setRequiredProvider] = useState<RequirableProvider | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [allowsSso, setAllowsSso] = useState(true)

  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<{ name?: string; providers?: string }>({})
  const [showSecret, setShowSecret] = useState(false)
  const [currentApp, setCurrentApp] = useState<Application | null>(null)
  const [activeTab, setActiveTab] = useState('general')

  // Redirect URLs state
  const [redirectUrls, setRedirectUrls] = useState<string[]>([])
  const [newRedirectUrl, setNewRedirectUrl] = useState('')
  const [redirectUrlError, setRedirectUrlError] = useState('')

  // Captcha configuration state
  const [captchaEnabled, setCaptchaEnabled] = useState(false)
  const [captchaSiteKey, setCaptchaSiteKey] = useState('')
  const [captchaSecretKey, setCaptchaSecretKey] = useState('')
  const [captchaRiskThreshold, setCaptchaRiskThreshold] = useState(50)
  const [showCaptchaSecret, setShowCaptchaSecret] = useState(false)

  useEffect(() => {
    if (isEdit && applications.length > 0) {
      const app = applications.find(a => a.id === id)
      if (app) {
        setCurrentApp(app)
        setName(app.name)
        setDescription(app.description || '')
        setImageUrl(app.image_url || '')
        setThemeColor(app.theme_color || '#A35BFF')
        setAllowedProviders(app.allowed_providers || ['email'])
        setRequiredProvider(app.required_provider || null)
        setIsActive(app.is_active)
        setAllowsSso(app.allows_sso ?? true)
        setRedirectUrls(app.redirect_urls || [])

        // Load captcha config if exists
        const captchaConfig = (app as any).captcha_config
        if (captchaConfig) {
          setCaptchaEnabled(captchaConfig.enabled || false)
          setCaptchaSiteKey(captchaConfig.site_key || '')
          setCaptchaSecretKey(captchaConfig.secret_key || '')
          setCaptchaRiskThreshold(captchaConfig.risk_threshold || 50)
        }
      }
    }
  }, [isEdit, id, applications])

  const toggleProvider = (provider: OAuthProvider) => {
    // Email is always required
    if (provider === 'email') return

    setAllowedProviders(prev => {
      if (prev.includes(provider)) {
        return prev.filter(p => p !== provider)
      }
      return [...prev, provider]
    })
  }

  const addRedirectUrl = () => {
    setRedirectUrlError('')
    const url = newRedirectUrl.trim()

    if (!url) {
      setRedirectUrlError('L\'URL est requise')
      return
    }

    // Validate URL format
    try {
      new URL(url)
    } catch {
      setRedirectUrlError('URL invalide')
      return
    }

    // Check for duplicates
    if (redirectUrls.includes(url)) {
      setRedirectUrlError('Cette URL existe déjà')
      return
    }

    setRedirectUrls(prev => [...prev, url])
    setNewRedirectUrl('')
  }

  const removeRedirectUrl = (urlToRemove: string) => {
    setRedirectUrls(prev => prev.filter(url => url !== urlToRemove))
  }

  const handleSubmit = async () => {
    const newErrors: { name?: string; providers?: string } = {}

    if (!name.trim()) {
      newErrors.name = 'Le nom de l\'application est requis'
    }

    if (allowedProviders.length === 0) {
      newErrors.providers = 'Au moins un fournisseur d\'authentification est requis'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setSaving(true)

    const data = {
      name: name.trim(),
      description: description.trim() || undefined,
      image_url: imageUrl || undefined,
      theme_color: themeColor,
      allowed_providers: allowedProviders,
      required_provider: requiredProvider,
      redirect_urls: redirectUrls,
      is_active: isActive,
      allows_sso: allowsSso,
      captcha_config: {
        enabled: captchaEnabled,
        provider: 'turnstile',
        site_key: captchaSiteKey || null,
        secret_key: captchaSecretKey || null,
        risk_threshold: captchaRiskThreshold
      }
    }

    if (isEdit && id) {
      const success = await updateApplication(id, data)
      if (success) {
        navigate('/platform/applications')
      } else {
        setErrors({ name: 'Échec de la mise à jour de l\'application' })
      }
    } else {
      const result = await createApplication(data)
      if (result) {
        navigate(`/platform/applications/${result.id}`)
      } else {
        setErrors({ name: 'Échec de la création de l\'application' })
      }
    }

    setSaving(false)
  }

  const handleRegenerateSecret = async () => {
    if (!currentApp) return
    if (confirm('Êtes-vous sûr ? Cela invalidera le secret actuel et pourrait casser les intégrations existantes.')) {
      await regenerateSecret(currentApp.id)
    }
  }

  return (
    <div className="page-content">
      <VStack gap="lg">
        <Animate type="fadeIn">
          <HStack gap="md" style={{ alignItems: 'center' }}>
            <IconButton
              icon={<ArrowLeft20Regular />}
              onClick={() => navigate('/platform/applications')}
            />
            <div>
              <Heading level={2}>{isEdit ? 'Modifier l\'application' : 'Nouvelle application'}</Heading>
              <Text color="muted">
                {isEdit ? 'Modifiez les paramètres de votre application' : 'Créez une nouvelle application pour Webba ID'}
              </Text>
            </div>
          </HStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          <Tabs
            tabs={[
              { id: 'general', label: 'Général', icon: <Info20Regular /> },
              { id: 'api', label: 'API & OAuth', icon: <Key20Regular /> },
              { id: 'providers', label: 'Authentification', icon: <People20Regular /> },
              { id: 'security', label: 'Sécurité', icon: <ShieldLock20Regular /> }
            ]}
            active={activeTab}
            onChange={setActiveTab}
            stretchLine
          />

          <div style={{ marginTop: '1.5rem' }}>
          <TabPanels active={activeTab}>
              {/* General Tab */}
              <TabPanel id="general" active={activeTab}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                      <Info20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={5}>Informations générales</Heading>
                    </HStack>

                    <div style={{ marginBottom: '-1rem' }}>
                      <Input
                        label="Nom de l'application"
                        placeholder="Mon Application"
                        value={name}
                        onChange={(v) => {
                          setName(v as string)
                          if (errors.name) setErrors(prev => ({ ...prev, name: undefined }))
                        }}
                        error={errors.name}
                      />
                    </div>

                    <div style={{ marginBottom: '-1rem' }}>
                      <Textarea
                        label="Description"
                        placeholder="Une brève description de votre application..."
                        value={description}
                        onChange={(v) => setDescription(v as string)}
                        rows={3}
                      />
                    </div>

                    <div>
                      <Text size="sm" weight="medium" style={{ marginBottom: '0.5rem' }}>
                        Image de l'application
                      </Text>
                      {imageUrl ? (
                        <Card variant="outlined" padding="md">
                          <HStack gap="md" style={{ alignItems: 'center' }}>
                            <img
                              src={imageUrl}
                              alt="Application"
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: 'var(--radius-md)'
                              }}
                            />
                            <VStack gap="md" style={{ flex: 1 }}>
                              <Text size="sm" color="muted">Image téléchargée</Text>
                              <Button
                                variant="danger"
                                size="sm"
                                icon={<Delete20Regular />}
                                onClick={async () => {
                                  await deleteImage(imageUrl)
                                  setImageUrl('')
                                }}
                              >
                                Supprimer
                              </Button>
                            </VStack>
                          </HStack>
                        </Card>
                      ) : (
                        <FileUpload
                          onFilesSelected={async (files: File[]) => {
                            if (files.length > 0) {
                              const url = await uploadImage(files[0])
                              if (url) setImageUrl(url)
                            }
                          }}
                          accept="image/*"
                          maxSize={5 * 1024 * 1024}
                        />
                      )}
                      {uploading && (
                        <Text size="sm" color="muted" style={{ marginTop: '0.5rem' }}>
                          Téléchargement en cours...
                        </Text>
                      )}
                    </div>

                    <ColorPicker
                      label="Couleur du thème"
                      value={themeColor}
                      onChange={setThemeColor}
                      showInput
                    />

                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text weight="medium">Statut de l'application</Text>
                        <Text size="sm" color="muted">
                          {isActive ? 'Les utilisateurs peuvent se connecter à cette application' : 'La connexion est désactivée'}
                        </Text>
                      </div>
                      <Switch
                        checked={isActive}
                        onChange={setIsActive}
                      />
                    </HStack>

                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text weight="medium">Autoriser SSO</Text>
                        <Text size="sm" color="muted">
                          {allowsSso ? 'Les organisations peuvent configurer le SSO pour cette application' : 'SSO désactivé pour cette application'}
                        </Text>
                      </div>
                      <Switch
                        checked={allowsSso}
                        onChange={setAllowsSso}
                      />
                    </HStack>
                  </VStack>
                </Card>
              </TabPanel>

              {/* API Tab */}
              <TabPanel id="api" active={activeTab}>
                <VStack gap="lg">
                  {/* API Credentials - only in edit mode */}
                  {isEdit && currentApp && (
                    <Card padding="lg">
                      <VStack gap="md">
                        <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                          <Key20Regular style={{ color: 'var(--brand-primary)' }} />
                          <Heading level={5}>Identifiants API</Heading>
                        </HStack>

                        <div style={{
                          backgroundColor: 'var(--bg-tertiary)',
                          borderRadius: 'var(--radius-md)',
                          padding: '1rem'
                        }}>
                          <VStack gap="md">
                            <div>
                              <Text size="sm" color="muted" style={{ marginBottom: '0.25rem' }}>
                                Clé d'application (client_id)
                              </Text>
                              <HStack gap="sm" style={{ alignItems: 'center' }}>
                                <code style={{
                                  flex: 1,
                                  padding: '0.5rem',
                                  backgroundColor: 'var(--bg-secondary)',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {currentApp.app_key}
                                </code>
                                <CopyButton text={currentApp.app_key} />
                              </HStack>
                            </div>

                            <div>
                              <Text size="sm" color="muted" style={{ marginBottom: '0.25rem' }}>
                                Secret d'application
                              </Text>
                              <HStack gap="sm" style={{ alignItems: 'center' }}>
                                <code style={{
                                  flex: 1,
                                  padding: '0.5rem',
                                  backgroundColor: 'var(--bg-secondary)',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {showSecret ? currentApp.app_secret : '••••••••••••••••••••••••••••••••'}
                                </code>
                                <IconButton
                                  icon={showSecret ? <EyeOff20Regular /> : <Eye20Regular />}
                                  onClick={() => setShowSecret(!showSecret)}
                                  size="sm"
                                />
                                <CopyButton text={currentApp.app_secret} />
                                <IconButton
                                  icon={<ArrowSync20Regular />}
                                  onClick={handleRegenerateSecret}
                                  size="sm"
                                />
                              </HStack>
                            </div>
                          </VStack>
                        </div>

                        <Card variant="outlined" padding="md" style={{ backgroundColor: 'var(--color-warning-bg)' }}>
                          <Text size="sm">
                            Ne partagez jamais votre secret d'application. Il permet d'authentifier les requêtes de votre serveur.
                          </Text>
                        </Card>
                      </VStack>
                    </Card>
                  )}

                  {/* Redirect URLs */}
                  <Card padding="lg">
                    <VStack gap="md">
                      <div>
                        <Heading level={5}>URLs de redirection (Callback)</Heading>
                        <Text size="sm" color="muted">
                          URLs autorisées pour le flow OAuth. Le SDK redirigera vers ces URLs après l'authentification.
                        </Text>
                      </div>

                      {/* Add new URL */}
                      <HStack gap="sm" style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <Input
                            placeholder="https://example.com/auth/callback"
                            value={newRedirectUrl}
                            onChange={(v) => {
                              setNewRedirectUrl(v as string)
                              setRedirectUrlError('')
                            }}
                            error={redirectUrlError}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                addRedirectUrl()
                              }
                            }}
                          />
                        </div>
                        <Button
                          icon={<Add20Regular />}
                          onClick={addRedirectUrl}
                          size="md"
                        >
                          Ajouter
                        </Button>
                      </HStack>

                      {/* URL list */}
                      {redirectUrls.length > 0 ? (
                        <VStack gap="xs">
                          {redirectUrls.map((url) => (
                            <div
                              key={url}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 0.75rem',
                                backgroundColor: 'var(--bg-tertiary)',
                                borderRadius: 'var(--radius-sm)'
                              }}
                            >
                              <code style={{
                                flex: 1,
                                fontSize: '0.85rem',
                                fontFamily: 'monospace',
                                wordBreak: 'break-all'
                              }}>
                                {url}
                              </code>
                              <IconButton
                                icon={<Dismiss20Regular />}
                                size="sm"
                                onClick={() => removeRedirectUrl(url)}
                              />
                            </div>
                          ))}
                        </VStack>
                      ) : (
                        <Card variant="outlined" padding="md">
                          <Text size="sm" color="muted" style={{ textAlign: 'center' }}>
                            Aucune URL de redirection configurée. Ajoutez au moins une URL pour utiliser le flow OAuth.
                          </Text>
                        </Card>
                      )}

                      <Card variant="outlined" padding="md" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                        <VStack gap="xs">
                          <Text size="sm" weight="medium">Exemples d'URLs valides:</Text>
                          <Text size="sm" color="muted">• http://localhost:5173/auth/callback (développement)</Text>
                          <Text size="sm" color="muted">• https://monapp.com/auth/callback (production)</Text>
                          <Text size="sm" color="muted">• https://app.example.com/api/auth/webba/callback</Text>
                        </VStack>
                      </Card>
                    </VStack>
                  </Card>
                </VStack>
              </TabPanel>

              {/* Providers Tab */}
              <TabPanel id="providers" active={activeTab}>
                <VStack gap="lg">
                  <Card padding="lg">
                    <VStack gap="md">
                      <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                        <People20Regular style={{ color: 'var(--brand-primary)' }} />
                        <Heading level={5}>Fournisseurs d'authentification</Heading>
                      </HStack>
                      {errors.providers && (
                        <Text size="sm" color="error">{errors.providers}</Text>
                      )}

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '0.75rem'
                      }}>
                        {OAUTH_PROVIDERS.map(provider => {
                          const isEmail = provider.id === 'email'
                          const isChecked = allowedProviders.includes(provider.id)

                          return (
                            <Card
                              key={provider.id}
                              padding="md"
                              variant={isChecked ? 'default' : 'outlined'}
                              hoverable={!isEmail}
                              onClick={() => toggleProvider(provider.id)}
                              style={{
                                cursor: isEmail ? 'not-allowed' : 'pointer',
                                borderColor: isChecked ? 'var(--brand-primary)' : undefined,
                                opacity: isEmail ? 0.7 : 1
                              }}
                            >
                              <HStack gap="sm" style={{ alignItems: 'center' }}>
                                <Checkbox
                                  checked={isChecked}
                                  onChange={() => toggleProvider(provider.id)}
                                  disabled={isEmail}
                                />
                                <Text weight="medium">{provider.label}</Text>
                                {isEmail && (
                                  <Text size="xs" color="muted">(requis)</Text>
                                )}
                              </HStack>
                            </Card>
                          )
                        })}
                      </div>
                    </VStack>
                  </Card>

                  <Card padding="lg">
                    <VStack gap="md">
                      <div>
                        <Heading level={5}>Fournisseur obligatoire</Heading>
                        <Text size="sm" color="muted">
                          Exiger qu'un fournisseur OAuth spécifique soit lié au compte de l'utilisateur pour accéder à cette application.
                        </Text>
                      </div>

                      <Select
                        label="Fournisseur requis"
                        value={requiredProvider || ''}
                        onChange={(v) => setRequiredProvider(v ? v as RequirableProvider : null)}
                        options={[
                          { value: '', label: 'Aucun (optionnel)' },
                          ...REQUIRABLE_PROVIDERS.map(p => ({ value: p.id, label: p.label }))
                        ]}
                      />

                      {requiredProvider && (
                        <Card variant="outlined" padding="md" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                          <VStack gap="xs">
                            <Text size="sm" weight="medium">Comportement:</Text>
                            <Text size="sm" color="muted">
                              • Si l'utilisateur n'a pas {REQUIRABLE_PROVIDERS.find(p => p.id === requiredProvider)?.label} lié à son compte, il devra le lier avant d'accéder à l'application.
                            </Text>
                            <Text size="sm" color="muted">
                              • La connexion par email seul ne sera pas suffisante.
                            </Text>
                            <Text size="sm" color="muted">
                              • L'utilisateur verra une page lui demandant de lier son compte {REQUIRABLE_PROVIDERS.find(p => p.id === requiredProvider)?.label}.
                            </Text>
                          </VStack>
                        </Card>
                      )}
                    </VStack>
                  </Card>
                </VStack>
              </TabPanel>

              {/* Security Tab */}
              <TabPanel id="security" active={activeTab}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                      <ShieldLock20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={5}>Captcha & Protection</Heading>
                    </HStack>

                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text weight="medium">Activer le Captcha</Text>
                        <Text size="sm" color="muted">
                          Afficher un captcha lorsque le score de risque est élevé
                        </Text>
                      </div>
                      <Switch
                        checked={captchaEnabled}
                        onChange={setCaptchaEnabled}
                      />
                    </HStack>

                    {captchaEnabled && (
                      <VStack gap="md" style={{
                        backgroundColor: 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        padding: '1rem'
                      }}>
                        <Input
                          label="Site Key (Clé publique)"
                          placeholder="0x4AAAAAAxxxxxx"
                          value={captchaSiteKey}
                          onChange={(v) => setCaptchaSiteKey(v as string)}
                        />

                        <div>
                          <Text size="sm" weight="medium" style={{ marginBottom: '0.5rem' }}>
                            Secret Key (Clé secrète)
                          </Text>
                          <HStack gap="sm">
                            <Input
                              type={showCaptchaSecret ? 'text' : 'password'}
                              placeholder="0x4AAAAAAxxxxxx"
                              value={captchaSecretKey}
                              onChange={(v) => setCaptchaSecretKey(v as string)}
                            />
                            <IconButton
                              icon={showCaptchaSecret ? <EyeOff20Regular /> : <Eye20Regular />}
                              onClick={() => setShowCaptchaSecret(!showCaptchaSecret)}
                              size="sm"
                            />
                          </HStack>
                        </div>

                        <div>
                          <Text size="sm" weight="medium" style={{ marginBottom: '0.5rem' }}>
                            Seuil de risque ({captchaRiskThreshold})
                          </Text>
                          <Text size="xs" color="muted" style={{ marginBottom: '0.5rem' }}>
                            Le captcha s'affiche lorsque le score de risque dépasse ce seuil (0-100)
                          </Text>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={captchaRiskThreshold}
                            onChange={(e) => setCaptchaRiskThreshold(Number(e.target.value))}
                            style={{ width: '100%' }}
                          />
                          <HStack style={{ justifyContent: 'space-between' }}>
                            <Text size="xs" color="muted">Plus sensible (0)</Text>
                            <Text size="xs" color="muted">Moins sensible (100)</Text>
                          </HStack>
                        </div>

                        <Text size="xs" color="muted">
                          Obtenez vos clés sur{' '}
                          <a
                            href="https://dash.cloudflare.com/?to=/:account/turnstile"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--brand-primary)' }}
                          >
                            Cloudflare Turnstile Dashboard
                          </a>
                        </Text>
                      </VStack>
                    )}
                  </VStack>
                </Card>
              </TabPanel>
            </TabPanels>
          </div>

          <HStack gap="md" style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
            <Button
              variant="secondary"
              onClick={() => navigate('/platform/applications')}
            >
              Annuler
            </Button>
            <Button
              icon={<Save20Regular />}
              loading={saving}
              onClick={handleSubmit}
            >
              {isEdit ? 'Enregistrer' : 'Créer l\'application'}
            </Button>
          </HStack>
        </Animate>
      </VStack>
    </div>
  )
}
